import { useState } from 'react';
import { InputByLevelRanking } from '../../../../features/gms-proposal-forms/coverage/level-ranking/coverage-type-level-ranking.state';
import { UniformCoverageTypes } from '../../../../features/gms-proposal-forms/coverage/uniform-coverage/coverage-type-uniform-coverage.state';
import { RowData } from '../../../../features/gms-proposal-forms/coverage/salary-rank/coverage-type-by-salary-rank.state';
import { formatNumberWithComma } from '../../../../utils/format-numbers.utils';

type ConfigurationType = 'coverage' | 'amountLoans' | 'none';

interface Props {
    currentConfigurationType: ConfigurationType;
    proposedConfigurationType: ConfigurationType;

    currentCoverageTypeName?: string | null;
    proposedCoverageTypeName?: string | null;

    currentIsLevelRanking?: boolean;
    proposedIsLevelRanking?: boolean;

    currentIsUniformCoverage?: boolean;
    proposedIsUniformCoverage?: boolean;

    currentIsSalaryRank?: boolean;
    proposedIsSalaryRank?: boolean;

    currentInputsByLevelRanking?: InputByLevelRanking[];
    proposedInputsByLevelRanking?: InputByLevelRanking[];

    currentInputsByUniformCoverage?: UniformCoverageTypes | null;
    proposedInputsByUniformCoverage?: UniformCoverageTypes | null;

    currentInputsBySalaryRank?: RowData[];
    proposedInputsBySalaryRank?: RowData[];

    currentAmountOfLoans?: string | null;
    proposedAmountOfLoans?: string | null;

    currentMinMaxAmount?: string | null;
    proposedMinMaxAmount?: string | null;

    currentLoanPortfolio?: string | null;
    proposedLoanPortfolio?: string | null;

    currentPaymentTerm?: string | null;
    proposedPaymentTerm?: string | null;

    currentLoanMaturity?: string | null;
    proposedLoanMaturity?: string | null;
}

type ChangeItem = {
    key: string;
    label: string;
    current: string;
    proposed: string;
};

const normalizeValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value).trim();
};

const normalizeNumber = (value: unknown): string => {
    const normalized = normalizeValue(value).replace(/,/g, '');

    if (!normalized) {
        return '';
    }

    const number = Number(normalized);

    return Number.isFinite(number)
        ? String(number)
        : normalized;
};

const deepEqual = (current: unknown, proposed: unknown): boolean => {
    return JSON.stringify(current) === JSON.stringify(proposed);
};

const normalizeLevelRanking = (
    value: InputByLevelRanking[] = [],
) => {
    return value
        .map((item) => ({
            label: normalizeValue(item.label),
            fAmount: normalizeNumber(item.fAmount),
        }))
        .sort((a, b) =>
            `${a.label}-${a.fAmount}`.localeCompare(
                `${b.label}-${b.fAmount}`,
            ),
        );
};

const normalizeUniformCoverage = (
    value?: UniformCoverageTypes | null,
) => {
    return {
        fAmount: normalizeNumber(value?.fAmount),
    };
};

const normalizeSalaryRank = (
    value: RowData[] = [],
) => {
    return value
        .map((item) => ({
            multiplierLabel: normalizeValue(item.multiplierLabel),
            salary: normalizeNumber(item.salary),
            result: normalizeNumber(item.result),
            designation: normalizeValue(item.designation),
        }))
        .sort((a, b) =>
            `${a.multiplierLabel}-${a.salary}-${a.result}-${a.designation}`.localeCompare(
                `${b.multiplierLabel}-${b.salary}-${b.result}-${b.designation}`,
            ),
        );
};

const getCoverageChanges = (props: Props): ChangeItem[] => {
    const {
        currentConfigurationType,
        proposedConfigurationType,
        currentCoverageTypeName,
        proposedCoverageTypeName,
        currentIsLevelRanking,
        proposedIsLevelRanking,
        currentIsUniformCoverage,
        proposedIsUniformCoverage,
        currentIsSalaryRank,
        proposedIsSalaryRank,
        currentInputsByLevelRanking = [],
        proposedInputsByLevelRanking = [],
        currentInputsByUniformCoverage,
        proposedInputsByUniformCoverage,
        currentInputsBySalaryRank = [],
        proposedInputsBySalaryRank = [],
    } = props;

    const changes: ChangeItem[] = [];

    const coverageConfigurationChanged =
        currentConfigurationType !== proposedConfigurationType;

    if (
        currentConfigurationType === 'coverage' ||
        proposedConfigurationType === 'coverage'
    ) {
        if (
            normalizeValue(currentCoverageTypeName) !==
            normalizeValue(proposedCoverageTypeName)
        ) {
            changes.push({
                key: 'coverage-type',
                label: 'Coverage Type',
                current: normalizeValue(currentCoverageTypeName),
                proposed: normalizeValue(proposedCoverageTypeName),
            });
        }

        const currentCoverageMethod =
            currentIsLevelRanking
                ? 'levelRanking'
                : currentIsUniformCoverage
                    ? 'uniformCoverage'
                    : currentIsSalaryRank
                        ? 'salaryRank'
                        : '';

        const proposedCoverageMethod =
            proposedIsLevelRanking
                ? 'levelRanking'
                : proposedIsUniformCoverage
                    ? 'uniformCoverage'
                    : proposedIsSalaryRank
                        ? 'salaryRank'
                        : '';

        if (
            coverageConfigurationChanged ||
            currentCoverageMethod !== proposedCoverageMethod
        ) {
            changes.push({
                key: 'coverage-method',
                label: 'Coverage Configuration',
                current:
                    currentConfigurationType === 'coverage'
                        ? currentCoverageMethod === 'levelRanking'
                            ? 'Level Ranking'
                            : currentCoverageMethod === 'uniformCoverage'
                                ? 'Uniform Coverage'
                                : currentCoverageMethod === 'salaryRank'
                                    ? 'Salary Rank'
                                    : 'Not provided'
                        : 'Not applicable',
                proposed:
                    proposedConfigurationType === 'coverage'
                        ? proposedCoverageMethod === 'levelRanking'
                            ? 'Level Ranking'
                            : proposedCoverageMethod === 'uniformCoverage'
                                ? 'Uniform Coverage'
                                : proposedCoverageMethod === 'salaryRank'
                                    ? 'Salary Rank'
                                    : 'Not provided'
                        : 'Not applicable',
            });
        }

        if (
            currentIsLevelRanking ||
            proposedIsLevelRanking
        ) {
            const currentValue = normalizeLevelRanking(
                currentConfigurationType === 'coverage' && currentIsLevelRanking
                    ? currentInputsByLevelRanking
                    : [],
            );

            const proposedValue = normalizeLevelRanking(
                proposedConfigurationType === 'coverage' && proposedIsLevelRanking
                    ? proposedInputsByLevelRanking
                    : [],
            );

            if (!deepEqual(currentValue, proposedValue)) {
                changes.push({
                    key: 'level-ranking',
                    label: 'Level Ranking',
                    current: JSON.stringify(currentValue),
                    proposed: JSON.stringify(proposedValue),
                });
            }
        }

        if (
            currentIsUniformCoverage ||
            proposedIsUniformCoverage
        ) {
            const currentValue = normalizeUniformCoverage(
                currentConfigurationType === 'coverage' &&
                currentIsUniformCoverage
                    ? currentInputsByUniformCoverage
                    : null,
            );

            const proposedValue = normalizeUniformCoverage(
                proposedConfigurationType === 'coverage' &&
                proposedIsUniformCoverage
                    ? proposedInputsByUniformCoverage
                    : null,
            );

            if (!deepEqual(currentValue, proposedValue)) {
                changes.push({
                    key: 'uniform-coverage',
                    label: 'Uniform Coverage',
                    current:
                        currentConfigurationType === 'coverage' &&
                        currentIsUniformCoverage
                            ? formatNumberWithComma(
                                currentInputsByUniformCoverage?.fAmount ?? '',
                            )
                            : '',
                    proposed:
                        proposedConfigurationType === 'coverage' &&
                        proposedIsUniformCoverage
                            ? formatNumberWithComma(
                                proposedInputsByUniformCoverage?.fAmount ?? '',
                            )
                            : '',
                });
            }
        }

        if (
            currentIsSalaryRank ||
            proposedIsSalaryRank
        ) {
            const currentValue = normalizeSalaryRank(
                currentConfigurationType === 'coverage' && currentIsSalaryRank
                    ? currentInputsBySalaryRank
                    : [],
            );

            const proposedValue = normalizeSalaryRank(
                proposedConfigurationType === 'coverage' && proposedIsSalaryRank
                    ? proposedInputsBySalaryRank
                    : [],
            );

            if (!deepEqual(currentValue, proposedValue)) {
                changes.push({
                    key: 'salary-rank',
                    label: 'Salary Rank',
                    current: JSON.stringify(currentValue),
                    proposed: JSON.stringify(proposedValue),
                });
            }
        }
    }

    return changes;
};

const getAmountLoanChanges = (props: Props): ChangeItem[] => {
    const {
        currentConfigurationType,
        proposedConfigurationType,
        currentAmountOfLoans,
        proposedAmountOfLoans,
        currentMinMaxAmount,
        proposedMinMaxAmount,
        currentLoanPortfolio,
        proposedLoanPortfolio,
        currentPaymentTerm,
        proposedPaymentTerm,
        currentLoanMaturity,
        proposedLoanMaturity,
    } = props;

    const changes: ChangeItem[] = [];

    if (
        currentConfigurationType === 'amountLoans' ||
        proposedConfigurationType === 'amountLoans'
    ) {
        if (
            normalizeValue(currentAmountOfLoans) !==
            normalizeValue(proposedAmountOfLoans)
        ) {
            changes.push({
                key: 'amount-of-loans',
                label: 'Amount of Loans',
                current:
                    currentConfigurationType === 'amountLoans'
                        ? normalizeValue(currentAmountOfLoans)
                        : '',
                proposed:
                    proposedConfigurationType === 'amountLoans'
                        ? normalizeValue(proposedAmountOfLoans)
                        : '',
            });
        }

        if (
            normalizeValue(currentMinMaxAmount) !==
            normalizeValue(proposedMinMaxAmount)
        ) {
            changes.push({
                key: 'minimum-maximum-loan',
                label: 'Minimum & Maximum Loan Amount',
                current:
                    currentConfigurationType === 'amountLoans'
                        ? normalizeValue(currentMinMaxAmount)
                        : '',
                proposed:
                    proposedConfigurationType === 'amountLoans'
                        ? normalizeValue(proposedMinMaxAmount)
                        : '',
            });
        }

        if (
            normalizeValue(currentLoanPortfolio) !==
            normalizeValue(proposedLoanPortfolio)
        ) {
            changes.push({
                key: 'loan-portfolio',
                label: 'Loan Portfolio',
                current:
                    currentConfigurationType === 'amountLoans'
                        ? normalizeValue(currentLoanPortfolio)
                        : '',
                proposed:
                    proposedConfigurationType === 'amountLoans'
                        ? normalizeValue(proposedLoanPortfolio)
                        : '',
            });
        }

        if (
            normalizeValue(currentPaymentTerm) !==
            normalizeValue(proposedPaymentTerm)
        ) {
            changes.push({
                key: 'payment-term',
                label: 'Payment Term',
                current:
                    currentConfigurationType === 'amountLoans'
                        ? normalizeValue(currentPaymentTerm)
                        : '',
                proposed:
                    proposedConfigurationType === 'amountLoans'
                        ? normalizeValue(proposedPaymentTerm)
                        : '',
            });
        }

        if (
            normalizeValue(currentLoanMaturity) !==
            normalizeValue(proposedLoanMaturity)
        ) {
            changes.push({
                key: 'loan-maturity',
                label: 'Loan Maturity',
                current:
                    currentConfigurationType === 'amountLoans'
                        ? normalizeValue(currentLoanMaturity)
                        : '',
                proposed:
                    proposedConfigurationType === 'amountLoans'
                        ? normalizeValue(proposedLoanMaturity)
                        : '',
            });
        }
    }

    return changes;
};

const ChangeItemView = ({
    item,
}: {
    item: ChangeItem;
}) => {
    return (
        <div className="grid grid-cols-1 gap-3 border-b border-gray-100 py-3 last:border-b-0 sm:grid-cols-2">
            <div className="min-w-0">
                <div className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                    {item.label}
                </div>

                <div className="break-words text-[11px] leading-relaxed text-gray-700">
                    {item.current || 'Not provided'}
                </div>
            </div>

            <div className="min-w-0 rounded-md bg-[#eef6fc] px-2.5 py-2">
                <div className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-[#186cb2]">
                    Proposed
                </div>

                <div className="break-words text-[11px] leading-relaxed text-gray-800">
                    {item.proposed || 'Not provided'}
                </div>
            </div>
        </div>
    );
};

const NoChanges = () => {
    return (
        <div className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-3 text-[11px] text-gray-400">
            <svg
                viewBox="0 0 24 24"
                fill="none"
                className="h-4 w-4 shrink-0"
                aria-hidden="true"
            >
                <path
                    d="m5 12.5 4.5 4L19 7"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>

            <span>No changes</span>
        </div>
    );
};

const LevelRankingPreview = ({
    title,
    inputs,
}: {
    title: string;
    inputs: InputByLevelRanking[];
}) => {
    return (
        <div className="overflow-x-auto rounded-md border border-gray-200">
            <div className="border-b border-[#186cb2]/20 bg-[#186cb2]/10 px-3 py-2">
                <p className="text-[10px] font-semibold text-gray-700">
                    {title}
                </p>
            </div>

            <table className="w-full min-w-[420px] border-collapse">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border border-gray-200 px-2 py-2 text-left text-[10px] font-medium text-gray-600">
                            Designation
                        </th>

                        <th className="border border-gray-200 px-2 py-2 text-left text-[10px] font-medium text-gray-600">
                            Average Salary
                        </th>
                    </tr>
                </thead>

                <tbody>
                    {inputs.length === 0 ? (
                        <tr>
                            <td
                                colSpan={2}
                                className="border border-gray-200 px-2 py-3 text-[10px] text-gray-400"
                            >
                                No data
                            </td>
                        </tr>
                    ) : (
                        inputs.map((input, index) => (
                            <tr key={`${input.label}-${index}`}>
                                <td className="border border-gray-200 px-2 py-2 text-[10px] text-gray-700">
                                    {input.label}
                                </td>

                                <td className="border border-gray-200 px-2 py-2 text-[10px] text-gray-700">
                                    {formatNumberWithComma(input.fAmount)}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

const UniformCoveragePreview = ({
    title,
    input,
}: {
    title: string;
    input?: UniformCoverageTypes | null;
}) => {
    return (
        <div className="overflow-hidden rounded-md border border-gray-200">
            <div className="border-b border-[#186cb2]/20 bg-[#186cb2]/10 px-3 py-2">
                <p className="text-[10px] font-semibold text-gray-700">
                    {title}
                </p>
            </div>

            <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2">
                <div>
                    <p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                        Average Salary
                    </p>

                    <p className="text-[11px] text-gray-700">
                        {formatNumberWithComma(input?.fAmount ?? '') || 'Not provided'}
                    </p>
                </div>
            </div>
        </div>
    );
};

const SalaryRankPreview = ({
    title,
    inputs,
}: {
    title: string;
    inputs: RowData[];
}) => {
    return (
        <div className="overflow-x-auto rounded-md border border-gray-200">
            <div className="border-b border-[#186cb2]/20 bg-[#186cb2]/10 px-3 py-2">
                <p className="text-[10px] font-semibold text-gray-700">
                    {title}
                </p>
            </div>

            <table className="w-full min-w-[560px] border-collapse">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border border-gray-200 px-2 py-2 text-left text-[10px] font-medium text-gray-600">
                            Salary Multiplier
                        </th>

                        <th className="border border-gray-200 px-2 py-2 text-left text-[10px] font-medium text-gray-600">
                            Average Salary
                        </th>

                        <th className="border border-gray-200 px-2 py-2 text-left text-[10px] font-medium text-gray-600">
                            Computation
                        </th>

                        <th className="border border-gray-200 px-2 py-2 text-left text-[10px] font-medium text-gray-600">
                            Designation
                        </th>
                    </tr>
                </thead>

                <tbody>
                    {inputs.length === 0 ? (
                        <tr>
                            <td
                                colSpan={4}
                                className="border border-gray-200 px-2 py-3 text-[10px] text-gray-400"
                            >
                                No data
                            </td>
                        </tr>
                    ) : (
                        inputs.map((input, index) => (
                            <tr key={`${input.designation}-${index}`}>
                                <td className="border border-gray-200 px-2 py-2 text-[10px] text-gray-700">
                                    {input.multiplierLabel}
                                </td>

                                <td className="border border-gray-200 px-2 py-2 text-[10px] text-gray-700">
                                    {formatNumberWithComma(input.salary)}
                                </td>

                                <td className="border border-gray-200 px-2 py-2 text-[10px] text-gray-700">
                                    {formatNumberWithComma(input.result)}
                                </td>

                                <td className="border border-gray-200 px-2 py-2 text-[10px] text-gray-700">
                                    {input.designation}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

const CoverageConfigurationView = ({
    configurationType,
    coverageTypeName,
    isLevelRanking,
    isUniformCoverage,
    isSalaryRank,
    inputsByLevelRanking = [],
    inputsByUniformCoverage,
    inputsBySalaryRank = [],
}: {
    configurationType: ConfigurationType;
    coverageTypeName?: string | null;
    isLevelRanking?: boolean;
    isUniformCoverage?: boolean;
    isSalaryRank?: boolean;
    inputsByLevelRanking?: InputByLevelRanking[];
    inputsByUniformCoverage?: UniformCoverageTypes | null;
    inputsBySalaryRank?: RowData[];
}) => {
    if (configurationType !== 'coverage') {
        return (
            <div className="rounded-md bg-gray-50 px-3 py-3 text-[11px] italic text-gray-400">
                Not applicable
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="rounded-md border border-gray-200 bg-white px-3 py-2.5">
                <p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                    Coverage Type
                </p>

                <p className="text-[11px] font-medium text-gray-700">
                    {coverageTypeName || 'Not provided'}
                </p>
            </div>

            {isLevelRanking && (
                <LevelRankingPreview
                    title={coverageTypeName || 'Level Ranking'}
                    inputs={inputsByLevelRanking}
                />
            )}

            {isUniformCoverage && (
                <UniformCoveragePreview
                    title={coverageTypeName || 'Uniform Coverage'}
                    input={inputsByUniformCoverage}
                />
            )}

            {isSalaryRank && (
                <SalaryRankPreview
                    title={coverageTypeName || 'Salary Rank'}
                    inputs={inputsBySalaryRank}
                />
            )}
        </div>
    );
};

const AmountLoansConfigurationView = ({
    configurationType,
    amountOfLoans,
    minMaxAmount,
    loanPortfolio,
    paymentTerm,
    loanMaturity,
}: {
    configurationType: ConfigurationType;
    amountOfLoans?: string | null;
    minMaxAmount?: string | null;
    loanPortfolio?: string | null;
    paymentTerm?: string | null;
    loanMaturity?: string | null;
}) => {
    if (configurationType !== 'amountLoans') {
        return (
            <div className="rounded-md bg-gray-50 px-3 py-3 text-[11px] italic text-gray-400">
                Not applicable
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-gray-200 bg-white px-3 py-2.5">
                <p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                    Amount of Loans
                </p>

                <p className="text-[11px] font-medium text-gray-700">
                    {amountOfLoans || 'Not provided'}
                </p>
            </div>

            <div className="rounded-md border border-gray-200 bg-white px-3 py-2.5">
                <p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                    Minimum & Maximum Loan Amount
                </p>

                <p className="text-[11px] font-medium text-gray-700">
                    {minMaxAmount || 'Not provided'}
                </p>
            </div>

            <div className="rounded-md border border-gray-200 bg-white px-3 py-2.5">
                <p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                    Loan Portfolio
                </p>

                <p className="text-[11px] font-medium text-gray-700">
                    {loanPortfolio || 'Not provided'}
                </p>
            </div>

            <div className="rounded-md border border-gray-200 bg-white px-3 py-2.5">
                <p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                    Payment Term
                </p>

                <p className="text-[11px] font-medium text-gray-700">
                    {paymentTerm || 'Not provided'}
                </p>
            </div>

            <div className="rounded-md border border-gray-200 bg-white px-3 py-2.5">
                <p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                    Loan Maturity
                </p>

                <p className="text-[11px] font-medium text-gray-700">
                    {loanMaturity || 'Not provided'}
                </p>
            </div>
        </div>
    );
};

export default function ProposedCoverageLoan({
    currentConfigurationType,
    proposedConfigurationType,
    currentCoverageTypeName,
    proposedCoverageTypeName,
    currentIsLevelRanking = false,
    proposedIsLevelRanking = false,
    currentIsUniformCoverage = false,
    proposedIsUniformCoverage = false,
    currentIsSalaryRank = false,
    proposedIsSalaryRank = false,
    currentInputsByLevelRanking = [],
    proposedInputsByLevelRanking = [],
    currentInputsByUniformCoverage,
    proposedInputsByUniformCoverage,
    currentInputsBySalaryRank = [],
    proposedInputsBySalaryRank = [],
    currentAmountOfLoans,
    proposedAmountOfLoans,
    currentMinMaxAmount,
    proposedMinMaxAmount,
    currentLoanPortfolio,
    proposedLoanPortfolio,
    currentPaymentTerm,
    proposedPaymentTerm,
    currentLoanMaturity,
    proposedLoanMaturity,
}: Props) {
    const [showContent, setShowContent] = useState(true);

    const coverageChanges = getCoverageChanges({
        currentConfigurationType,
        proposedConfigurationType,
        currentCoverageTypeName,
        proposedCoverageTypeName,
        currentIsLevelRanking,
        proposedIsLevelRanking,
        currentIsUniformCoverage,
        proposedIsUniformCoverage,
        currentIsSalaryRank,
        proposedIsSalaryRank,
        currentInputsByLevelRanking,
        proposedInputsByLevelRanking,
        currentInputsByUniformCoverage,
        proposedInputsByUniformCoverage,
        currentInputsBySalaryRank,
        proposedInputsBySalaryRank,
    });

    const amountLoanChanges = getAmountLoanChanges({
        currentConfigurationType,
        proposedConfigurationType,
        currentAmountOfLoans,
        proposedAmountOfLoans,
        currentMinMaxAmount,
        proposedMinMaxAmount,
        currentLoanPortfolio,
        proposedLoanPortfolio,
        currentPaymentTerm,
        proposedPaymentTerm,
        currentLoanMaturity,
        proposedLoanMaturity,
    });

    const coverageChanged = coverageChanges.length > 0;

    const amountLoansChanged = amountLoanChanges.length > 0;

    const configurationChanged =
        currentConfigurationType !== proposedConfigurationType ||
        coverageChanged ||
        amountLoansChanged;

    if (!configurationChanged) {
        return null;
    }

    return (
        <div className="grid grid-cols-1 gap-3.5">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                {/* ================================================= */}
                {/* SECTION HEADER */}
                {/* ================================================= */}
                <button
                    type="button"
                    onClick={() => setShowContent((previous) => !previous)}
                    className="flex w-full items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2.5 text-left transition-colors hover:bg-slate-50"
                >
                    <div className="flex min-w-0 items-center gap-2">
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            className={`h-3.5 w-3.5 shrink-0 text-[#186cb2] transition-transform ${showContent ? 'rotate-0' : '-rotate-90'}`}
                            aria-hidden="true"
                        >
                            <path
                                d="m7 10 5 5 5-5"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>

                        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                            Coverage & Loan Configuration
                        </h3>
                    </div>

                    <span className="shrink-0 text-[9px] font-medium text-slate-400">
                        {showContent ? 'Hide' : 'Show'}
                    </span>
                </button>

                {showContent && (
                    <>
                        {/* ================================================= */}
                        {/* CURRENT / PROPOSED */}
                        {/* ================================================= */}
                        <div className="grid grid-cols-1 border-b border-slate-200 sm:grid-cols-2">
                            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 sm:border-b-0 sm:border-r">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    Current
                                </h3>
                            </div>

                            <div className="bg-[#f5f9fc] px-4 py-2.5">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[#186cb2]">
                                    Proposed
                                </h3>
                            </div>
                        </div>

                        {/* ================================================= */}
                        {/* CONTENT */}
                        {/* ================================================= */}
                        <div className="grid grid-cols-1 sm:grid-cols-2">
                            {/* CURRENT */}
                            <div className="min-w-0 border-b border-slate-200 p-4 sm:border-b-0 sm:border-r">
                                <div className="space-y-3">
                                    {currentConfigurationType === 'coverage' && (
                                        <CoverageConfigurationView
                                            configurationType="coverage"
                                            coverageTypeName={currentCoverageTypeName}
                                            isLevelRanking={currentIsLevelRanking}
                                            isUniformCoverage={currentIsUniformCoverage}
                                            isSalaryRank={currentIsSalaryRank}
                                            inputsByLevelRanking={currentInputsByLevelRanking}
                                            inputsByUniformCoverage={currentInputsByUniformCoverage}
                                            inputsBySalaryRank={currentInputsBySalaryRank}
                                        />
                                    )}

                                    {currentConfigurationType === 'amountLoans' && (
                                        <AmountLoansConfigurationView
                                            configurationType="amountLoans"
                                            amountOfLoans={currentAmountOfLoans}
                                            minMaxAmount={currentMinMaxAmount}
                                            loanPortfolio={currentLoanPortfolio}
                                            paymentTerm={currentPaymentTerm}
                                            loanMaturity={currentLoanMaturity}
                                        />
                                    )}

                                    {currentConfigurationType === 'none' && (
                                        <div className="rounded-md bg-gray-50 px-3 py-3 text-[11px] italic text-gray-400">
                                            Not applicable
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* PROPOSED */}
                            <div className="min-w-0 p-4">
                                <div className="space-y-3">
                                    {proposedConfigurationType === 'coverage' && (
                                        <CoverageConfigurationView
                                            configurationType="coverage"
                                            coverageTypeName={proposedCoverageTypeName}
                                            isLevelRanking={proposedIsLevelRanking}
                                            isUniformCoverage={proposedIsUniformCoverage}
                                            isSalaryRank={proposedIsSalaryRank}
                                            inputsByLevelRanking={proposedInputsByLevelRanking}
                                            inputsByUniformCoverage={proposedInputsByUniformCoverage}
                                            inputsBySalaryRank={proposedInputsBySalaryRank}
                                        />
                                    )}

                                    {proposedConfigurationType === 'amountLoans' && (
                                        <AmountLoansConfigurationView
                                            configurationType="amountLoans"
                                            amountOfLoans={proposedAmountOfLoans}
                                            minMaxAmount={proposedMinMaxAmount}
                                            loanPortfolio={proposedLoanPortfolio}
                                            paymentTerm={proposedPaymentTerm}
                                            loanMaturity={proposedLoanMaturity}
                                        />
                                    )}

                                    {proposedConfigurationType === 'none' && (
                                        <div className="rounded-md bg-gray-50 px-3 py-3 text-[11px] italic text-gray-400">
                                            Not applicable
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}